import * as React from 'react';

import {
  IconRegularChevronLeft,
  IconRegularChevronRight,
  SkeletonElement,
} from '@notino/react-styleguide';
import { Video } from '@notino/shared/definitions/types';

import { ScrollThumbs } from '@containers/ProductDetailContainer/ProductDetail/ProductImageGallery/Thumbs/ScrollThumbs';
import { getVideoIframe } from '@containers/ProductDetailContainer/utils';
import { IImageProperties } from '@helpers/utils';
import { Arrow } from '@hooks/useArrows/styled';

import { useImageZoom } from './hooks/useImageZoom';
import { useMediaNavigation } from './hooks/useMediaNavigation';
import {
  Wrapper,
  CarouselTrack,
  Slide,
  PlayerWrapper,
  ThumbsWrapper,
  VideoSizeContainer,
  ZoomContainer,
  Image,
} from './styled';

interface IModalGalleryProps {
  onClose: () => void;
  imageIndex: number;
  images: IImageProperties[];
  thumbImages: IImageProperties[];
  videos?: Video[];
}

export const ModalGallery: React.FC<IModalGalleryProps> = ({
  imageIndex = 0,
  thumbImages,
  videos,
  onClose,
  images,
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const shouldAnimate = React.useRef(true);
  const isHoverEnabled = React.useRef(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      isHoverEnabled.current = true;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const { isZoomed, zoomContainerRef, zoomLevel, handleZoomClick, resetZoom } =
    useImageZoom();

  const {
    mediaIndex,
    setMediaIndex,
    isFirstItem,
    isLastItem,
    currentImage: _image,
    currentVideo: _video,
    isLoading,
    imageRef,
    handleImageLoad,
  } = useMediaNavigation({
    imageIndex,
    images,
    thumbImages,
    videos,
    onClose,
    onMediaChange: resetZoom,
  });

  const handleArrowPrev = React.useCallback(() => {
    shouldAnimate.current = true;
    setMediaIndex((i) => i - 1);
  }, [setMediaIndex]);

  const handleArrowNext = React.useCallback(() => {
    shouldAnimate.current = true;
    setMediaIndex((i) => i + 1);
  }, [setMediaIndex]);

  const handleThumbChange = React.useCallback(
    (index: number) => {
      if (!isHoverEnabled.current) {
        return;
      }
      shouldAnimate.current = false;
      setMediaIndex(index);
    },
    [setMediaIndex]
  );

  const renderSlide = (index: number) => {
    // Only render the active slide and its immediate neighbors (for the slide animation).
    // All other slides are empty placeholders to preserve layout.
    const isNearby = Math.abs(index - mediaIndex) <= 1;
    if (!isNearby) {
      return <Slide key={`empty-${index}`} />;
    }

    const videoIndex = index - images.length;
    const isVideo = videoIndex >= 0;
    const isActive = index === mediaIndex;

    if (isVideo) {
      // Only render video content for the active slide to prevent audio playing in background
      if (!isActive) {
        return <Slide key={`video-${videoIndex}`} />;
      }
      const video = videos?.[videoIndex];
      if (!video) {
        return <Slide key={`video-${videoIndex}`} />;
      }
      const videoHtml = getVideoIframe(video, true);
      return (
        <Slide key={`video-${videoIndex}`}>
          <VideoSizeContainer>
            <PlayerWrapper
              dangerouslySetInnerHTML={{
                __html: videoHtml,
              }}
            />
          </VideoSizeContainer>
        </Slide>
      );
    }

    const slideImage = images[index];
    return (
      <Slide key={`image-${index}`}>
        <ZoomContainer
          ref={isActive ? zoomContainerRef : undefined}
          data-zoomed={isActive && isZoomed}
          onClick={isActive ? handleZoomClick : undefined}
        >
          {isActive && isLoading && <SkeletonElement height={30} width={35} />}
          <Image
            ref={isActive ? imageRef : undefined}
            data-testid={isActive ? 'modal-main-image' : undefined}
            src={slideImage?.src}
            srcSet={slideImage?.srcSet}
            alt={slideImage?.alt || ''}
            onLoad={isActive ? handleImageLoad : undefined}
            style={{
              display: isActive && isLoading ? 'none' : 'block',
              transform:
                isActive && isZoomed ? `scale(${zoomLevel})` : 'scale(1)',
              transformOrigin: 'var(--zoom-origin, center center)',
            }}
          />
        </ZoomContainer>
      </Slide>
    );
  };

  return (
    <>
      <Wrapper
        ref={modalRef}
        tabIndex={-1}
        data-testid="modal-gallery"
        role="dialog"
        aria-modal={true}
        aria-label="Product media gallery"
      >
        {!isFirstItem && !isZoomed && (
          <Arrow
            position="left"
            onClick={handleArrowPrev}
            role="button"
            style={{ left: '1.25rem' }}
          >
            <IconRegularChevronLeft
              color="icon.primary"
              height="1.25rem"
              width="1.25rem"
            />
          </Arrow>
        )}

        <CarouselTrack
          data-zoomed={isZoomed}
          style={{
            transform: `translateX(-${mediaIndex * 100}%)`,
            transition: shouldAnimate.current ? undefined : 'none',
          }}
        >
          {thumbImages.map((_, index) => renderSlide(index))}
        </CarouselTrack>

        {!isLastItem && !isZoomed && (
          <Arrow
            position="right"
            onClick={handleArrowNext}
            role="button"
            style={{ right: '1.25rem' }}
          >
            <IconRegularChevronRight
              color="icon.primary"
              height="1.25rem"
              width="1.25rem"
            />
          </Arrow>
        )}
      </Wrapper>

      {thumbImages.length > 1 && (
        <ThumbsWrapper>
          <ScrollThumbs
            thumbSize="5rem"
            thumbGap="0.5rem"
            isCentered={true}
            imagesAttrs={thumbImages}
            imageIndex={mediaIndex}
            handleImageChange={handleThumbChange}
            dataTestId="modal-gallery-slider"
          />
        </ThumbsWrapper>
      )}
    </>
  );
};
